import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { ShieldAlert, ShieldCheck, AlertTriangle, RefreshCw, Loader2, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '../lib/errorUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Pre-flight Validation Panel — runs before "Process Run".
 * Surfaces actionable BLOCKERS (Staff missing CTC, O&M missing skill/rate)
 * and WARNINGS (Pay-Days fallback). Inline; collapsible.
 */
export default function PayrollPreflightPanel({ runId, onOk }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);


  // Auto-run once when runId changes.
  const check = useCallback(async () => {
    if (!runId) return;
    setLoading(true);
    try {
      const { data: res } = await axios.get(
        `${API_URL}/payroll/runs/${runId}/preflight`, { withCredentials: true });
      setData(res);
      if (onOk) onOk(res.ok);
      if (res.ok && (!res.warnings || res.warnings.length === 0)) {
        toast.success('Pre-flight passed — ready to process');
      } else if (res.ok) {
        toast.info(`Pre-flight passed with ${res.warnings.length} warning(s)`);
      } else {
        toast.warning(`${res.blockers.length} blocker(s) found`);
      }
      setOpen(true);
    } catch (e) {
      toast.error(formatApiError(e) || 'Pre-flight check failed');
    } finally { setLoading(false); }
  }, [runId, onOk]);

  useEffect(() => { if (runId) check(); }, [runId, check]);

  if (!data && !loading) return null;

  const okBadge = data?.ok && (!data.warnings || data.warnings.length === 0);
  const blocker = !data?.ok;

  return (
    <div
      data-testid="payroll-preflight"
      className={`border rounded-lg ${
        loading ? 'bg-slate-50 border-slate-200'
        : blocker ? 'bg-rose-50 border-rose-200'
        : okBadge ? 'bg-emerald-50 border-emerald-200'
        : 'bg-amber-50 border-amber-200'
      }`}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full px-4 py-3 flex items-center justify-between text-sm font-semibold"
        data-testid="preflight-toggle"
      >
        <span className="flex items-center gap-2">
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          {loading ? (
            <><Loader2 className="w-4 h-4 animate-spin text-slate-500" /> <span className="text-slate-700">Running pre-flight…</span></>
          ) : blocker ? (
            <><ShieldAlert className="w-4 h-4 text-rose-600" /> <span className="text-rose-800">Pre-flight: {data.blockers.length} blocker(s) — payroll WILL fail</span></>
          ) : okBadge ? (
            <><ShieldCheck className="w-4 h-4 text-emerald-600" /> <span className="text-emerald-800">Pre-flight passed — {data.summary.total_employees} employees scoped, {data.summary.groups_active} groups active</span></>
          ) : (
            <><AlertTriangle className="w-4 h-4 text-amber-600" /> <span className="text-amber-800">Pre-flight passed with {data.warnings.length} warning(s)</span></>
          )}
        </span>
        <span className="flex items-center gap-1">
          <button onClick={(e) => { e.stopPropagation(); check(); }}
            disabled={loading}
            data-testid="preflight-refresh"
            className="p-1 text-slate-500 hover:text-slate-700">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </span>
      </button>

      {open && data && (
        <div className="px-4 pb-3 space-y-2">
          {data.blockers.map((b, i) => (
            <Issue key={`b-${i}`} kind="blocker" item={b} />
          ))}
          {data.warnings.map((w, i) => (
            <Issue key={`w-${i}`} kind="warning" item={w} />
          ))}
          <div className="text-[10px] text-slate-500 pt-2 border-t border-slate-200/50 flex flex-wrap gap-x-4 gap-y-1">
            <span>Total: <b>{data.summary.total_employees}</b></span>
            <span>Staff missing CTC: <b className={data.summary.staff_missing_ctc ? 'text-rose-600' : ''}>{data.summary.staff_missing_ctc}</b></span>
            <span>O&amp;M missing skill: <b className={data.summary.om_missing_skill ? 'text-rose-600' : ''}>{data.summary.om_missing_skill}</b></span>
            <span>O&amp;M missing rate: <b className={data.summary.om_missing_rate ? 'text-rose-600' : ''}>{data.summary.om_missing_rate}</b></span>
            <span>Groups missing pay-days: <b className={data.summary.pay_days_groups_missing ? 'text-amber-600' : ''}>{data.summary.pay_days_groups_missing}</b></span>
          </div>
        </div>
      )}
    </div>
  );
}

function Issue({ kind, item }) {
  const styles = kind === 'blocker'
    ? 'bg-white border-rose-200 text-rose-900'
    : 'bg-white border-amber-200 text-amber-900';
  return (
    <div className={`border ${styles} rounded p-2.5 text-xs`} data-testid={`preflight-${kind}-${item.code}`}>
      <div className="flex items-start gap-2">
        <span className={`shrink-0 mt-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${
          kind === 'blocker' ? 'bg-rose-600 text-white' : 'bg-amber-500 text-white'
        }`}>{kind}</span>
        <div className="flex-1">
          <p className="font-semibold">{item.message}</p>
          <p className="text-[11px] opacity-80 mt-0.5">→ {item.fix}</p>
          {item.samples?.length > 0 && (
            <p className="text-[10px] mt-1 font-mono opacity-70 truncate" title={item.samples.join(', ')}>
              e.g. {item.samples.slice(0, 5).join(', ')}{item.samples.length > 5 ? ` (+${item.samples.length - 5} more)` : ''}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
